import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import DashboardShell from "../components/dashboard/DashboardShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { IMAGES } from "../mock";

const PENGGUNA_MENU = [
  { label: "Dashboard", icon: "dashboard", to: "/dashboard/pengguna", exact: true },
  { label: "Pesanan Saya", icon: "clipboard", to: "/pengguna/pesanan", badgeKey: "orders" },
  { label: "Pencarian Inspector", icon: "search", to: "/pengguna/cari" },
  { label: "Favorit", icon: "heart", to: "/pengguna/favorit" },
  { label: "Chat", icon: "chat", to: "/pengguna/chat", badgeKey: "chat" },
  { label: "Laporan Saya", icon: "file", to: "/pengguna/laporan" },
  { label: "Pembayaran", icon: "creditcard", to: "/pengguna/pembayaran" },
  { label: "Ulasan Saya", icon: "star", to: "/pengguna/ulasan" },
  { label: "Notifikasi", icon: "bell", to: "/pengguna/notifikasi", badgeKey: "notif" },
  { label: "Akun Saya", icon: "usercircle", to: "/pengguna/akun" },
  { label: "Keluar", icon: "logout", action: "logout", testId: "keluar" },
];

const INSPECTOR_MENU = [
  { label: "Dashboard", icon: "dashboard", to: "/dashboard/inspector", exact: true },
  { label: "Pesanan Masuk", icon: "inbox", to: "/inspector/pesanan", badgeKey: "orders" },
  { label: "Jadwal Saya", icon: "calendar", to: "/inspector/jadwal" },
  { label: "Laporan Inspeksi", icon: "file", to: "/inspector/laporan" },
  { label: "Pendapatan", icon: "wallet", to: "/inspector/pendapatan" },
  { label: "Pencairan Dana", icon: "banknote", to: "/inspector/pencairan" },
  { label: "Profil & Layanan", icon: "usercog", to: "/inspector/profil" },
  { label: "Chat", icon: "chat", to: "/inspector/chat", badgeKey: "chat" },
  { label: "Rating & Ulasan", icon: "star", to: "/inspector/ulasan" },
  { label: "Notifikasi", icon: "bell", to: "/inspector/notifikasi", badgeKey: "notif" },
  { label: "Keluar", icon: "logout", action: "logout", testId: "keluar" },
];

const ADMIN_MENU = [
  { label: "Dashboard", icon: "dashboard", to: "/dashboard/admin", exact: true },
  { label: "Manajemen Pengguna", icon: "users", to: "/admin/pengguna" },
  { label: "Manajemen Inspector", icon: "usercheck", to: "/admin/inspector" },
  { label: "Manajemen Pesanan", icon: "clipboard", to: "/admin/pesanan" },
  { label: "Verifikasi Dokumen", icon: "filecheck", to: "/admin/verifikasi", badgeKey: "verif" },
  { label: "Keuangan & Komisi", icon: "banknote", to: "/admin/keuangan", badgeKey: "payouts" },
  { label: "Pengaturan", icon: "settings", to: "/admin/pengaturan" },
  { label: "Notifikasi", icon: "bell", to: "/admin/notifikasi", badgeKey: "notif" },
  { label: "Keluar", icon: "logout", action: "logout", testId: "keluar" },
];

function useBadges(role) {
  const { data: notif } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 15000 });
  const { data: chats } = useQuery({ queryKey: ["chats"], queryFn: () => api.get("/chats").then((r) => r.data), refetchInterval: 15000, enabled: role !== "admin" });
  const { data: orders } = useQuery({ queryKey: ["orders", role === "inspector" ? "menunggu_konfirmasi" : "menunggu_bayar,menunggu_review"],
    queryFn: () => api.get("/orders", { params: { status: role === "inspector" ? "menunggu_konfirmasi" : "menunggu_bayar,menunggu_review" } }).then((r) => r.data), enabled: role !== "admin", refetchInterval: 20000 });
  const { data: admin } = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api.get("/admin/dashboard").then((r) => r.data), enabled: role === "admin", refetchInterval: 30000 });
  return {
    notif: notif?.unread || 0,
    chat: chats?.reduce((s, c) => s + (c.unread || 0), 0) || 0,
    orders: orders?.length || 0,
    verif: admin?.stats?.pending_verification || 0,
    payouts: admin?.stats?.pending_payouts || 0,
  };
}

function SidebarPromo() {
  return (
    <div className="relative rounded-2xl overflow-hidden">
      <img src={IMAGES.heroAlt} alt="Inspector" className="w-full h-40 object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1736] via-[#0b1736]/60 to-transparent" />
      <p className="absolute bottom-3 left-3 right-3 text-white text-[13px] font-bold leading-snug">Bersama Inspecar, Kendaraan Lebih Aman di Tangan Profesional.</p>
    </div>
  );
}

export function PenggunaLayout() {
  const { user } = useAuth();
  const badges = useBadges("pengguna");
  return (
    <DashboardShell menu={PENGGUNA_MENU} locationPill={user?.city || "Indonesia"} notifPath="/pengguna/notifikasi" badges={badges}>
      <Outlet />
    </DashboardShell>
  );
}

export function InspectorLayout() {
  const qc = useQueryClient();
  const badges = useBadges("inspector");
  const { data: dash } = useQuery({ queryKey: ["inspector-dashboard"], queryFn: () => api.get("/inspectors/me/dashboard").then((r) => r.data) });
  const [optimistic, setOptimistic] = useState(null);
  const online = optimistic ?? dash?.is_online ?? false;
  const toggle = async (v) => {
    setOptimistic(v);
    try {
      await api.put("/inspectors/me/online", { is_online: v });
      toast.success(v ? "Anda sekarang Online" : "Anda sekarang Offline", { duration: 1500 });
      qc.invalidateQueries({ queryKey: ["inspector-dashboard"] });
    } catch { setOptimistic(!v); toast.error("Gagal mengubah status"); }
  };
  return (
    <DashboardShell menu={INSPECTOR_MENU} dark bottomCard={<SidebarPromo />} notifPath="/inspector/notifikasi" badges={badges}
      onlinePill={{ value: online, onChange: toggle }}>
      <Outlet />
    </DashboardShell>
  );
}

export function AdminLayout() {
  const badges = useBadges("admin");
  return (
    <DashboardShell menu={ADMIN_MENU} dark logoBadge="Super Admin" notifPath="/admin/notifikasi" badges={badges}>
      <Outlet />
    </DashboardShell>
  );
}
